/**
 * useDubSync.js
 *
 * Custom hook that encapsulates all DubSync operations inside the Video Editor:
 *  - syncMagic()       — transcribe + align dubbed audio, load cues into editor
 *  - renderAudio()     — render synced audio to MP3, add as audio track
 *  - saveTimeline()    — persist current cue timings via API
 *
 * Uses EditorContext for state dispatch and VideoProcessingContext for toast-style
 * progress visibility.  All operations are async and non-blocking.
 */

import { useCallback } from 'react';
import { useVideoProcessing } from '../../../contexts/VideoProcessingContext';
import videoCRUDServiceInstance from '../../../services/VideoCRUDService';
import { useEditor } from '../EditorContext';
import { autoSplitCues } from './dubSyncUtils';

// Lightweight in-memory log collector for DubSync flows. Accessible from
// the browser console as `window.__getDubSyncLogs()` / `window.__clearDubSyncLogs()`
if (typeof window !== 'undefined') {
    window.__dubsyncLogs = window.__dubsyncLogs || [];
    window.__getDubSyncLogs = () => window.__dubsyncLogs;
    window.__clearDubSyncLogs = () => { window.__dubsyncLogs.length = 0; };
    window.__downloadDubSyncLogs = () => {
        try {
            const blob = new Blob([JSON.stringify(window.__dubsyncLogs, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `dubsync-logs-${new Date().toISOString()}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to download DubSync logs', e);
        }
    };
}

function _log(level, message, meta) {
    const entry = { ts: new Date().toISOString(), level, message, meta };
    try {
        if (typeof window !== 'undefined' && window.__dubsyncLogs) window.__dubsyncLogs.push(entry);
    } catch (_) {}
    // Mirror to console for immediate visibility
    if (level === 'error') console.error('[DubSync]', message, meta || '');
    else if (level === 'warn') console.warn('[DubSync]', message, meta || '');
    else console.info('[DubSync]', message, meta || '');
}
// ── Helpers ───────────────────────────────────────────────────────────────────

function secondsToVttTime(totalSeconds) {
    const h  = Math.floor(totalSeconds / 3600);
    const m  = Math.floor((totalSeconds % 3600) / 60);
    const s  = Math.floor(totalSeconds % 60);
    const ms = Math.round((totalSeconds % 1) * 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

export function buildVttFromCues(cues) {
    let vtt = 'WEBVTT\n\n';
    cues.forEach((cue, idx) => {
        vtt += `${idx + 1}\n`;
        vtt += `${secondsToVttTime(cue.start ?? cue.startTime)} --> ${secondsToVttTime(cue.end ?? cue.endTime)}\n`;
        vtt += `${cue.text}\n\n`;
    });
    return vtt;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * @param {string} videoId
 * @param {string} videoTitle
 */
export function useDubSync(videoId, videoTitle) {
    const {
        state,
        updateDubSyncTrack,
        setDubSyncCues,
        addAudioTrack,
    } = useEditor();

    const { trackVideo } = useVideoProcessing();

    /**
     * "Sync Magic" — transcribes the dubbed audio then aligns timings.
     * The track must be in READY state before calling.
     *
     * @param {string} language   — base language (e.g. "Hindi")
     * @param {File|null} uploadedFile — supplied when language has no dubbingTransformedUrl
     * @param {{ forceTranscribe?: boolean, noCheapMode?: boolean, withSrtHint?: boolean }} options
     *   forceTranscribe — bypass cache and always re-run transcription
     *   noCheapMode     — send transcription without cheap_mode (full-quality)
     *   withSrtHint     — pass the original SRT to the AI server as a timing hint
     */
    const syncMagic = useCallback(async (language, uploadedFile = null, options = {}) => {
        updateDubSyncTrack(language, { trackState: 'SYNCING', error: null });

        // Show the video processing panel so the user can see activity
        trackVideo(videoId, videoTitle || 'Video');

        const startTs = Date.now();
        _log('info', 'syncMagic.start', { videoId, language, forceTranscribe: !!options.forceTranscribe, noCheapMode: !!options.noCheapMode, withSrtHint: !!options.withSrtHint, uploadedFile: uploadedFile ? { name: uploadedFile.name, size: uploadedFile.size } : null });

        try {
            const dubSyncLanguageName = `${language}-DubSync`;

            // ── Pre-check: is transcription already done from a previous run? ──────
            // This avoids calling the expensive transcription API again.
            let transcriptionAlreadyDone = false;
            try {
                const checkResp = await videoCRUDServiceInstance.getVideoById(videoId);
                const checkVl = Array.isArray(checkResp?.data?.videoLanguages)
                    ? checkResp.data.videoLanguages.find((x) => x.languageName === dubSyncLanguageName)
                    : null;
                if (checkVl && ((checkVl.srtFileUrl && checkVl.srtFileUrl.trim()) || (checkVl.vttFileUrl && checkVl.vttFileUrl.trim()))) {
                    transcriptionAlreadyDone = true;
                    _log('info', 'syncMagic.transcriptionAlreadyDone', { language, dubSyncLanguageName, srt: checkVl.srtFileUrl, vtt: checkVl.vttFileUrl });
                }
            } catch (e) {
                _log('warn', 'syncMagic.preCheckFailed', { language, error: e?.message });
            }

            if (!transcriptionAlreadyDone || options.forceTranscribe) {
                if (options.forceTranscribe && transcriptionAlreadyDone) {
                    _log('info', 'syncMagic.forceTranscribe', { language, dubSyncLanguageName });
                }
                // ── Step 1: Transcribe ─────────────────────────────────────────
                const transcribeResp = await videoCRUDServiceInstance.dubSyncTranscribe(
                    videoId,
                    language,
                    uploadedFile,
                    !!options.noCheapMode,
                    !!options.withSrtHint,
                );
                _log('info', 'transcribe.response', { status: transcribeResp?.status, data: transcribeResp?.data });
                if (!transcribeResp?.data) throw new Error('Transcription returned no data');

                // The transcription work is queued and processed asynchronously on the server.
                // Poll until the {language}-DubSync entry has an SRT/VTT (or until timeout).
                // Poll every 30s, timeout after 20x the video's duration.
                const pollIntervalMs = 30000; // 30s
                const videoDurationSec = typeof state?.duration === 'number' && state.duration > 0 ? state.duration : 60;
                const maxWaitMs = Math.max(30000, Math.round(20 * videoDurationSec * 1000));
                const startWait = Date.now();

                let ready = false;
                let lastVideoResp = null;
                _log('info', 'transcription.poll.start', { language, pollIntervalMs, maxWaitMs, videoDurationSec });
                while (Date.now() - startWait < maxWaitMs) {
                    try {
                        const vidResp = await videoCRUDServiceInstance.getVideoById(videoId);
                        lastVideoResp = vidResp;
                        const vdata = vidResp?.data;
                        const vl = Array.isArray(vdata?.videoLanguages)
                            ? vdata.videoLanguages.find((x) => x.languageName === dubSyncLanguageName)
                            : null;
                        if (vl) {
                            if ((vl.srtFileUrl && vl.srtFileUrl.trim()) || (vl.vttFileUrl && vl.vttFileUrl.trim())) {
                                ready = true;
                                _log('info', 'transcription.poll.ready', { language, dubSyncLanguageName, srt: vl.srtFileUrl, vtt: vl.vttFileUrl });
                                break;
                            }
                            if (vl.dubSyncStatus && vl.dubSyncStatus.toUpperCase() === 'FAILED') {
                                _log('warn', 'transcription.poll.serverFailed', { language, dubSyncLanguageName, status: vl.dubSyncStatus });
                                break;
                            }
                        }
                    } catch (e) {
                        _log('warn', 'transcription.poll.error', { language, error: e?.message });
                    }
                    await new Promise((res) => setTimeout(res, pollIntervalMs));
                }

                if (!ready) {
                    throw new Error('Transcription not ready yet (timed out). Please retry Sync Magic in a few seconds.');
                }
            }

            // ── Step 2: Align ──────────────────────────────────────────────
            const alignResp = await videoCRUDServiceInstance.dubSyncAlign(videoId, language);
            _log('info', 'align.response', { status: alignResp?.status, data: alignResp?.data });
            if (!alignResp?.data) throw new Error('Alignment returned no data');

            // If the backend returned updated videoData with persisted audio URL, store
            // that URL on the editor track so subsequent steps don't require re-upload.
            try {
                const videoData = alignResp?.data?.videoData;
                const baseLang = Array.isArray(videoData?.videoLanguages)
                    ? videoData.videoLanguages.find((l) => l.languageName === language)
                    : null;
                const persistedAudioUrl = baseLang?.dubbingTransformedUrl;
                if (persistedAudioUrl) {
                    try {
                        updateDubSyncTrack(language, { persistedAudioUrl, uploadedAudioFile: null });
                        _log('info', 'syncMagic.persistedAudioSaved', { language, persistedAudioUrl });

                        // Add the raw dubbed audio as an audio track so the user can play it
                        // in the timeline while reviewing cue alignment.
                        const rawTrackId = `dubsync-raw-${language}`;
                        const alreadyAdded = (state.audioTracks || []).some((t) => t.id === rawTrackId);
                        if (!alreadyAdded) {
                            addAudioTrack({
                                id:            rawTrackId,
                                name:          `🎙 ${language} (dubbed)`,
                                url:           persistedAudioUrl,
                                volume:        1,
                                offsetSeconds: 0,
                                muted:         false,
                            });
                            _log('info', 'syncMagic.dubbedAudioTrackAdded', { language, persistedAudioUrl });
                        }
                    } catch (e) {
                        _log('warn', 'syncMagic.persistedAudioSaveFailed', { language, error: e?.message });
                    }
                }
            } catch (e) {
                // Non-fatal — continue
            }

            const {
                originalCues    = [],
                dubSyncCues     = [],
                rawWhisperCues  = [],
                problematicCues = [],
                perCueRatios    = [],
            } = alignResp.data;

            _log('info', 'align.cues.sample', {
                originalTotal: originalCues.length,
                dubSyncTotal: dubSyncCues.length,
                origFirst3: originalCues.slice(0, 3).map((c) => ({ id: c.id, start: c.start, end: c.end })),
                dsFirst3: dubSyncCues.slice(0, 3).map((c) => ({ id: c.id, start: c.start, end: c.end })),
                dsLast1: dubSyncCues.slice(-1).map((c) => ({ id: c.id, start: c.start, end: c.end })),
            });

            // Normalize backend {start, end} field names → frontend {startTime, endTime}
            // so autoSplitCues (which reads startTime/endTime) works correctly.
            const normCue = (c) => ({ id: c.id, startTime: c.start, endTime: c.end, text: c.text });
            const normOriginalCues   = originalCues.map(normCue);
            const normDubSyncCues    = dubSyncCues.map(normCue);
            const normRawWhisperCues = rawWhisperCues.map(normCue);

            // Auto-split only when the backend returned significantly fewer cues
            // than the original (e.g. Whisper produced 5 large chunks for 203
            // originals).  When the counts already match (align did its job),
            // skip the split — redistributing an already well-aligned 1:1 result
            // would scramble both the text and the timing.
            const needsSplit =
                normOriginalCues.length > 0 &&
                normDubSyncCues.length > 0 &&
                normDubSyncCues.length < normOriginalCues.length * 0.8; // >20% fewer cues → split
            const finalDubSyncCues = needsSplit
                ? autoSplitCues(normDubSyncCues, normOriginalCues)
                : normDubSyncCues;

            // Loads cues into subtitlesByLanguage['{language}-DubSync'] and
            // updates dubSyncTracks entry → trackState = 'SYNCED'
            // Also stores raw Whisper cues in subtitlesByLanguage['{language}-Whisper']
            setDubSyncCues(language, finalDubSyncCues, normOriginalCues, problematicCues, perCueRatios, normRawWhisperCues);

            _log('info', 'syncMagic.finished', {
                language,
                cues: finalDubSyncCues.length,
                autoSplit: needsSplit,
                problematic: (problematicCues || []).length,
                elapsedMs: Date.now() - startTs,
            });
        } catch (err) {
            _log('error', 'syncMagic.error', {
                language,
                message: err?.message,
                response: err?.response?.data,
            });
            updateDubSyncTrack(language, {
                trackState: 'FAILED',
                error: err?.response?.data?.error || err.message || 'Sync Magic failed',
            });
        }
    }, [videoId, videoTitle, updateDubSyncTrack, setDubSyncCues, trackVideo]);

    /**
     * Render synced audio to MP3 and add it as a new audio track in the editor.
     * The track must be in SYNCED state before calling.
     *
     * @param {string} language
     */
    const renderAudio = useCallback(async (language) => {
        updateDubSyncTrack(language, { trackState: 'RENDERING', error: null });

        const startTs = Date.now();
        try {
            const dubSyncLangKey = `${language}-DubSync`;
            const cues = state.subtitlesByLanguage[dubSyncLangKey] || [];

            if (cues.length === 0) throw new Error('No cues to render — run Sync Magic first');

            const vttContent = buildVttFromCues(cues);
            // Log timing samples so you can verify that your edited positions are
            // actually being sent — compare first/last cue times against the timeline.
            _log('info', 'renderAudio.start', {
                language,
                cues: cues.length,
                vttLength: vttContent.length,
                firstCue:  cues[0]  ? { start: cues[0].startTime  ?? cues[0].start,  end: cues[0].endTime  ?? cues[0].end,  text: (cues[0].text  || '').slice(0, 60) } : null,
                lastCue:   cues[cues.length - 1] ? { start: cues[cues.length-1].startTime ?? cues[cues.length-1].start, end: cues[cues.length-1].endTime ?? cues[cues.length-1].end, text: (cues[cues.length-1].text || '').slice(0, 60) } : null,
                vttPreview: vttContent.slice(0, 400), // first ~3 cues visible in console
            });

            const renderResp = await videoCRUDServiceInstance.dubSyncRenderAudio(
                videoId,
                language,
                vttContent,
            );
            _log('info', 'render.response', {
                status: renderResp?.status,
                cuesProcessed: renderResp?.data?.cuesProcessed,
                cuesFailed: renderResp?.data?.cuesFailed,
                problematicCount: renderResp?.data?.problematicIndices?.length,
                problematicIndices: renderResp?.data?.problematicIndices,
                totalMs: renderResp?.data?.totalMs,
            });
            if (!renderResp?.data) throw new Error('Render returned no data');

            const {
                renderedAudioUrl,
                problematicIndices = [],
            } = renderResp.data;

            if (!renderedAudioUrl) throw new Error('Render succeeded but no audio URL returned');

            // Add rendered MP3 as a regular audio track so it appears in the timeline
            addAudioTrack({
                id:            `dubsync-rendered-${language}-${Date.now()}`,
                name:          `${language} DubSync`,
                url:           renderedAudioUrl,
                volume:        1,
                offsetSeconds: 0,
                muted:         false,
            });

            updateDubSyncTrack(language, {
                trackState:      'RENDERED',
                renderedAudioUrl,
                problematicCues: problematicIndices,
                error:           null,
            });

            _log('info', 'renderAudio.finished', { language, renderedAudioUrl, problematicCount: (problematicIndices || []).length, elapsedMs: Date.now() - startTs });
        } catch (err) {
            _log('error', 'renderAudio.error', { language, message: err?.message, response: err?.response?.data });
            updateDubSyncTrack(language, {
                trackState: 'SYNCED', // revert so user can retry
                error: err?.response?.data?.error || err.message || 'Audio render failed',
            });
        }
    }, [videoId, state.subtitlesByLanguage, updateDubSyncTrack, addAudioTrack]);

    /**
     * Persist the current cue timings to the backend.
     *
     * @param {string} language
     * @param {Array}  [cuesToSave]  Optional: pass cues directly when calling immediately
     *                               after a state update (avoids stale-closure reads).
     */
    const saveTimeline = useCallback(async (language, cuesToSave) => {
        const dubSyncLangKey = `${language}-DubSync`;
        // Prefer the explicitly provided cues (avoids stale state when called right after
        // updateDubSyncCues, before React re-renders and refreshes the callback closure).
        const cues = cuesToSave || state.subtitlesByLanguage[dubSyncLangKey] || [];
        if (cues.length === 0) return;

        const vttContent = buildVttFromCues(cues);
        _log('info', 'saveTimeline.start', { language, cues: cues.length, vttLength: vttContent.length });
        try {
            const resp = await videoCRUDServiceInstance.dubSyncSaveTimeline(videoId, language, vttContent);
            _log('info', 'saveTimeline.finished', { language, status: resp?.status, data: resp?.data });
        } catch (err) {
            _log('error', 'saveTimeline.error', { language, message: err?.message, response: err?.response?.data });
            // Non-fatal — editor state is still intact
        }
    }, [videoId, state.subtitlesByLanguage]);

    // ── Step 2 (Manual path) – Bootstrap DubSync from base language subtitles ─

    /**
     * Bootstrap a DubSync track from the base language subtitle VTT.
     * Sets preAlignVttUrl on the backend so renderAudio can compute tempo.
     * Returns cues in the same format as syncMagic so the track enters SYNCED state.
     *
     * @param {string} language  Base language name (e.g. "Hindi")
     */
    const bootstrapManual = useCallback(async (language) => {
        updateDubSyncTrack(language, { trackState: 'SYNCING', error: null });
        _log('info', 'bootstrapManual.start', { videoId, language });
        try {
            const resp = await videoCRUDServiceInstance.dubSyncBootstrapManual(videoId, language);
            const { dubSyncCues = [], originalCues = [] } = resp.data;
            // Backend returns cues with { id, start, end, text } — normalise to editor shape
            const normCue = (c) => ({
                id: c.id,
                startTime: c.start ?? c.startTime,
                endTime: c.end ?? c.endTime,
                text: c.text,
            });
            const normalised = dubSyncCues.map(normCue);
            setDubSyncCues(
                language,
                normalised,
                originalCues.map(normCue),
                [], // problematicCues
                [], // perCueRatios
                [], // rawWhisperCues
            );
            _log('info', 'bootstrapManual.finished', { language, cues: normalised.length });
            return normalised; // caller can use these without waiting for a re-render
        } catch (err) {
            _log('error', 'bootstrapManual.error', { language, message: err?.message, response: err?.response?.data });
            updateDubSyncTrack(language, {
                trackState: 'FAILED',
                error: err?.response?.data?.error || err.message || 'Bootstrap failed',
            });
            return null;
        }
    }, [videoId, updateDubSyncTrack, setDubSyncCues]);

    return { syncMagic, renderAudio, saveTimeline, bootstrapManual };
}
